import { Component } from '@theme/component';
import { CartItemsComponent } from '@theme/component-cart-items';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { fetchConfig, lockScroll, unlockScroll } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import {
  fetchDiscountRequirementText,
  fetchDiscountInfo,
  fetchMemberVouchers,
  formatMemberVoucherLine,
  fillTemplate,
} from '@theme/discount-info-api';
import { CartDiscountUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {object} CartDiscountRefs
 * @property {HTMLDialogElement} dialog - The "Select Voucher" picker.
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 * @property {HTMLElement} exclusiveBanner - Proactive "can't combine" warning, hidden by default.
 * @property {HTMLElement} exclusiveBannerText - Its message.
 * @property {HTMLButtonElement} exclusiveBannerAction - Its optional "Hapus {code}" quick action.
 * @property {HTMLElement} triggerCount - The collapsed trigger row's "{n} voucher tersimpan" line.
 */

/**
 * localStorage key for the vouchers the shopper UNCHECKED in this picker.
 *
 * Unchecking has to detach the code from the cart (Shopify has no "attached but off" state), and a
 * code that is not attached is invisible to Liquid, so before this the voucher just vanished from the
 * list and had to be typed in again. The code is remembered here instead and offered back as an
 * unchecked, enabled pill (see #renderSavedVouchers).
 *
 * Scoped to ONE cart: the entry is `{ token, codes }` and is ignored (and overwritten on the next
 * save) as soon as the cart's own `token` differs — a finished order starts a new cart, so a voucher
 * remembered for the old one never leaks into it. Local to this browser by design; nothing is sent
 * anywhere.
 */
const SAVED_VOUCHERS_KEY = 'ldc-saved-vouchers';

/** More than this many remembered codes for one cart is noise, not a wallet. */
const SAVED_VOUCHERS_MAX = 20;

/**
 * @returns {{ token: string, codes: string[] } | null}
 */
function readSavedVouchersEntry() {
  try {
    const parsed = JSON.parse(localStorage.getItem(SAVED_VOUCHERS_KEY) ?? 'null');
    if (!parsed || typeof parsed.token !== 'string' || !Array.isArray(parsed.codes)) return null;
    return { token: parsed.token, codes: parsed.codes.filter((/** @type {unknown} */ c) => typeof c === 'string' && c) };
  } catch {
    return null;
  }
}

/**
 * @param {string} token - The current cart's token.
 * @returns {string[]} The codes remembered for THIS cart (empty for any other).
 */
function readSavedVouchers(token) {
  const entry = readSavedVouchersEntry();
  return entry && entry.token === token ? entry.codes : [];
}

/**
 * @param {string} token
 * @param {string[]} codes
 */
function writeSavedVouchers(token, codes) {
  try {
    if (codes.length === 0) localStorage.removeItem(SAVED_VOUCHERS_KEY);
    else localStorage.setItem(SAVED_VOUCHERS_KEY, JSON.stringify({ token, codes: codes.slice(-SAVED_VOUCHERS_MAX) }));
  } catch {
    // Storage blocked or full (private window, quota): the picker just behaves as it did before.
  }
}

/**
 * @param {string} a
 * @param {string} b
 */
const sameCode = (a, b) => a.toUpperCase() === b.toUpperCase();

/**
 * A custom element that opens a "Select Voucher" picker and applies/removes discount codes from it.
 *
 * The picker owns its own raw `<dialog>` directly rather than nesting a `dialog-component` — same
 * reason as `assets/cart-line-variant-picker.js` (see that file's own class doc comment): a
 * declarative `on:click`/`on:change` resolves to the *closest* Component ancestor, so a checkbox or
 * button inside a nested `dialog-component` would dispatch to IT instead of here. Escape and focus
 * trapping come from the native `<dialog>`; scroll lock and backdrop-click are added by hand, and
 * `close` (which doesn't bubble) is listened for directly on this element.
 *
 * @extends {Component<CartDiscountRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = [
    'dialog',
    'cartDiscountError',
    'cartDiscountErrorDiscountCode',
    'cartDiscountErrorShipping',
    'exclusiveBanner',
    'exclusiveBannerText',
    'exclusiveBannerAction',
    'triggerCount',
  ];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #pending = false;

  #needsRefresh = false;

  connectedCallback() {
    super.connectedCallback();
    this.#reconcileIneligibleCodes();
    this.addEventListener('click', this.#onBackdropClick);
    // `close` doesn't bubble, so this has to sit directly on the element that owns the dialog rather
    // than relying on delegation — see the class doc comment above for why there's no dialog-component
    // here to provide this for free the way it does elsewhere in this theme.
    this.addEventListener('close', this.#onDialogClose, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onBackdropClick);
    this.removeEventListener('close', this.#onDialogClose, true);
    unlockScroll(this);
  }

  /**
   * Fires whenever a morph touches this component's subtree (see assets/component.js) — e.g. a
   * quantity change elsewhere on the cart page re-renders this section. A morph always replaces
   * anything #reconcileIneligibleCodes previously cloned in, with Liquid's own (still ineligible-blind)
   * markup, so this re-adds it every time rather than assuming a page-load call is enough.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#reconcileIneligibleCodes();
    // A morph re-renders the list from Liquid's member-voucher-blind markup too (nothing in this
    // theme's own templates knows about the membership backend), so a still-open picker needs this
    // re-run after every apply/remove or its remaining unclaimed vouchers would vanish.
    this.#reconcileMemberVouchers();
  }

  openPicker = () => {
    // showModal() throws on a dialog that's already open — cheap insurance against a rapid double
    // click on the trigger row re-invoking this before the first call has rendered.
    if (this.refs.dialog.open) return;
    this.refs.dialog.showModal();
    lockScroll(this);
    // Lazy, not on connect: a logged-in member's claimed vouchers are only worth fetching once
    // they're actually looking at the list, since each call can cost the membership backend a
    // handful of live Shopify Admin API calls (see #reconcileMemberVouchers's own doc comment).
    this.#reconcileMemberVouchers();
  };

  closeDialog = () => {
    this.refs.dialog.close();
  };

  /**
   * Keeps discount controls focusable while the pending guard prevents another mutation.
   * @returns {() => void} Restores the controls' previous states.
   */
  #disableControls() {
    const previousBusy = this.getAttribute('aria-busy');
    const input = this.querySelector('input[name="discount"]');
    const previousReadOnly = input instanceof HTMLInputElement && input.readOnly;
    const buttons = Array.from(this.querySelectorAll('button'), (button) => ({
      button,
      previousDisabled: button.getAttribute('aria-disabled'),
    }));

    this.setAttribute('aria-busy', 'true');
    if (input instanceof HTMLInputElement) input.readOnly = true;
    for (const { button } of buttons) button.setAttribute('aria-disabled', 'true');

    return () => {
      if (previousBusy === null) this.removeAttribute('aria-busy');
      else this.setAttribute('aria-busy', previousBusy);

      if (input instanceof HTMLInputElement) input.readOnly = previousReadOnly;
      for (const { button, previousDisabled } of buttons) {
        if (previousDisabled === null) button.removeAttribute('aria-disabled');
        else button.setAttribute('aria-disabled', previousDisabled);
      }
    };
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Refreshes this section when a cart AJAX response cannot include rendered section HTML.
   * Cancelled renders can resolve without validation, so retry until this refresh reaches the DOM.
   * @param {(html: string) => boolean} [shouldRender] - Validates the rendered discounts before morphing.
   */
  async #refreshDiscountSection(shouldRender) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    this.#needsRefresh = true;
    let validated = false;

    try {
      while (!validated && this.isConnected) {
        let pendingQuantityUpdates = CartItemsComponent.pendingQuantityUpdates;
        while (pendingQuantityUpdates.length) {
          await Promise.all(pendingQuantityUpdates);
          pendingQuantityUpdates = CartItemsComponent.pendingQuantityUpdates;
        }
        if (!this.isConnected) return;

        await sectionRenderer.renderSection(sectionId, {
          cache: false,
          mode: this.closest('theme-drawer') ? 'hydration' : 'full',
          shouldRender: (html) => {
            validated = true;
            return shouldRender?.(html) ?? true;
          },
        });
      }

      if (validated) this.#needsRefresh = false;
    } catch (error) {
      if (error?.name === 'AbortError') return;

      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Failed to refresh cart discounts',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    }
  }

  /**
   * Replaces stale pills before accepting another action against the refreshed controls.
   */
  async #refreshStaleDiscounts() {
    this.#pending = true;
    const restoreControls = this.#disableControls();

    try {
      await this.#refreshDiscountSection();
    } finally {
      this.#pending = false;
      restoreControls();
    }
  }

  /**
   * Handles submitting a new discount code from the picker's manual entry field.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement)) return;

    try {
      await this.#submitCode(discountCode.value, discountCode);
    } finally {
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Checking an "available to claim" member-voucher pill applies it immediately — the same request
   * a manually-typed code goes through, just sourced from the membership system's own redemption
   * record instead of the input field (see #reconcileMemberVouchers). Only the checked transition is
   * meaningful: these pills start unchecked and enabled, with nothing to become MORE checked than.
   * @param {Event} event
   */
  claimMemberVoucher = async (event) => {
    const checkbox = event.target;
    if (!(checkbox instanceof HTMLInputElement) || !checkbox.checked) return;

    const pill = checkbox.closest('.cart-discount__pill');
    const code = pill instanceof HTMLElement ? pill.dataset.claimCode : undefined;
    if (!code) return;

    // Another cart-discount mutation is still in flight (see #pending): ignore the click and put the
    // box back, rather than leaving it checked for an apply that is never going to happen.
    if (this.#pending) {
      checkbox.checked = false;
      return;
    }

    // Optimistic-disable while the request is in flight, so a fast second click can't submit twice —
    // a morph replaces this pill's markup entirely once the real cart state comes back either way.
    checkbox.disabled = true;
    await this.#submitCode(code);

    // A successful apply morphs the list, which replaces this pill. Any other outcome (the
    // exclusivity banner, an error, a request that never went out) leaves THIS pill on screen ticked
    // and disabled — put it back so it can be tried again. `data-claim-code` still being present is
    // what tells "still my pill" from "morphed into the applied pill" (morph strips it).
    if (pill instanceof HTMLElement && pill.isConnected && pill.hasAttribute('data-claim-code')) {
      checkbox.checked = false;
      checkbox.disabled = false;
    }
  };

  /**
   * Shared core of submitting ONE discount code for the cart to try applying — used by the manual
   * entry form (`applyDiscount`) and an "available to claim" member-voucher pill's checkbox
   * (`claimMemberVoucher`).
   *
   * One cart-discount mutation at a time (`#pending`), and a section an earlier response left stale
   * (`#needsRefresh`) is refreshed before anything new is accepted. The guard covers the whole call —
   * including the exclusivity pre-flight in `#applyCode`, which awaits a network lookup — so a second
   * click can't slip in before the request itself has started.
   * @param {string} code
   * @param {HTMLInputElement} [inputToClear] - The manual entry field, when this came from it.
   */
  async #submitCode(code, inputToClear) {
    if (typeof this.dataset.sectionId !== 'string') return;
    if (this.#pending) return;

    if (this.#needsRefresh) {
      await this.#refreshStaleDiscounts();
      return;
    }

    this.#pending = true;
    const restoreControls = this.#disableControls();

    try {
      await this.#applyCode(code, inputToClear);
    } finally {
      this.#pending = false;
      restoreControls();
    }
  }

  /**
   * The request half of `#submitCode`. Not guarded on its own — only call it through `#submitCode`.
   * @param {string} code
   * @param {HTMLInputElement} [inputToClear] - The manual entry field, when this came from it.
   */
  async #applyCode(code, inputToClear) {
    if (typeof this.dataset.sectionId !== 'string') return;

    const existingDiscounts = this.#existingDiscounts();
    if (existingDiscounts.includes(code)) return;

    this.#hideExclusiveBanner();
    const conflict = await this.#findExclusivityConflict(code, existingDiscounts);
    if (conflict) {
      this.#showExclusiveBanner(conflict);
      return;
    }

    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    sectionRenderer.abortRender(this.dataset.sectionId);
    const abortController = this.#createAbortController();

    cartDiscountError.classList.add('hidden');
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');

    const allDiscountCodes = [...existingDiscounts, code];
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    try {
      this.dispatchEvent(
        new CartDiscountUpdateEvent({
          discountCodes: allDiscountCodes.map((c) => ({ code: c })),
          promise: deferredPromise.promise,
        })
      );

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: allDiscountCodes.join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === code && discount.applicable === false;
        })
      ) {
        // The <input> carries data-skip-node-update, so the morph below never touches its value —
        // safe to clear it before or after.
        if (inputToClear) inputToClear.value = '';
        deferredPromise.resolve({
          cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
        });
        // snippets/cart-summary.liquid now renders every code Shopify has attached to the cart, not
        // only ones with an active allocation (see that file's own note) — so the code just entered
        // DOES still deserve a pill here, an "ineligible" one the shopper can see and remove, right
        // next to the inline error below. Without this morph it would stay invisible until the next
        // full page load, which is the exact "customer can't tell what happened to their code" gap
        // this whole change exists to close.
        const ineligibleHtml = data.sections?.[this.dataset.sectionId];
        if (ineligibleHtml) {
          sectionRenderer.abortRender(this.dataset.sectionId);
          await morphSection(this.dataset.sectionId, ineligibleHtml, {
            mode: this.closest('theme-drawer') ? 'hydration' : 'full',
          });
        } else {
          // The response carried no rendered section (see #refreshDiscountSection) — fetch one.
          await this.#refreshDiscountSection();
        }
        await this.#restoreListAfterMorph();
        // Called AFTER the morph, not before: the error container has no data-skip-node-update, so a
        // morph reconciles its class list against the server's always-hidden default markup — calling
        // this first would show the error for a moment and have the morph immediately hide it again.
        this.#handleDiscountError('discount_code');
        return;
      }

      const sections = /** @type {Record<string, string> | null | undefined} */ (data.sections);
      const newHtml = sections?.[this.dataset.sectionId];

      /**
       * Before morphing, we need to check if the shipping discount is applicable in the UI: the
       * Liquid logic is compared to the cart payload to assess whether we leveraged a valid shipping
       * discount code (Liquid omits shipping discounts the payload still reports as applicable).
       * @param {string} html - The rendered section HTML.
       * @returns {boolean} Whether the section can replace the current discount UI.
       */
      const shouldRender = (html) => {
        const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
        const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
        const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
        if (section) {
          const codes = Array.from(discountCodes)
            .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
            .filter(Boolean);
          if (
            codes.length === existingDiscounts.length &&
            codes.every((/** @type {string} */ c) => existingDiscounts.includes(c)) &&
            data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
              return discount.code === code && discount.applicable === true;
            })
          ) {
            this.#handleDiscountError('shipping');
            return false;
          }
        }

        return true;
      };

      if (!newHtml) {
        if (inputToClear) inputToClear.value = '';
        deferredPromise.resolve({
          cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
        });
        await this.#refreshDiscountSection(shouldRender);
        await this.#restoreListAfterMorph();
        return;
      }

      if (!shouldRender(newHtml)) {
        if (inputToClear) inputToClear.value = '';
        deferredPromise.resolve({
          cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
        });
        return;
      }

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });
      // Clear the input explicitly: data-skip-node-update on <input name="discount"> means
      // morphSection no longer syncs the input value from the server-rendered empty state,
      // so without this the user's typed code stays in the field after a successful apply.
      if (inputToClear) inputToClear.value = '';
      sectionRenderer.abortRender(this.dataset.sectionId);
      await morphSection(this.dataset.sectionId, newHtml, {
        mode: this.closest('theme-drawer') ? 'hydration' : 'full',
      });
      await this.#restoreListAfterMorph();
    } catch (error) {
      deferredPromise.reject(error);
      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to apply discount',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handles removing a discount from the cart via the ineligible pill's × button. An eligible pill
   * has no such button any more — unchecking its checkbox (`toggleDiscount`) is the picker's own
   * remove control for a code that's actually doing something.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    this.#forgetSavedVoucher(discountCode);
    await this.#performRemoval(discountCode, { save: false });
  };

  /**
   * Handles an eligible pill's checkbox being unchecked — the picker's own remove control for a code
   * that's currently applied. Only the checked→unchecked transition is meaningful: the box starts
   * checked and there's nothing to become MORE checked than, so a stray re-check (e.g. a fast second
   * click before the pending morph settles) is simply ignored and left for that morph to reconcile.
   * @param {Event} event
   */
  toggleDiscount = async (event) => {
    if (typeof this.dataset.sectionId !== 'string') return;

    const checkbox = event.target;
    if (!(checkbox instanceof HTMLInputElement) || checkbox.checked) return;

    // Another cart-discount mutation is still in flight (see #pending): the code is still applied, so
    // put the box back to match instead of showing it unchecked.
    if (this.#pending) {
      checkbox.checked = true;
      return;
    }

    const pill = checkbox.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    await this.#performRemoval(discountCode, { save: true });
  };

  /**
   * The exclusivity banner's own quick action (only rendered when the conflict is against an
   * already-applied code, see `#findExclusivityConflict`) — removes that one code, then hides the
   * banner so the shopper can press Apply again themselves. Deliberately does not auto-resubmit
   * whatever they typed: no hidden retry chains, and the input still holds it either way.
   * @param {MouseEvent} event
   */
  removeConflictingDiscount = async (event) => {
    const button = event.target;
    this.#hideExclusiveBanner();
    if (!(button instanceof HTMLButtonElement)) return;

    const discountCode = button.dataset.discountCode;
    if (!discountCode) return;

    await this.#performRemoval(discountCode, { save: true });
  };

  /**
   * The × on a saved (unchecked) voucher: forget it for good. Nothing touches the cart — the code is
   * not attached to it.
   * @param {Event} event
   */
  forgetSavedVoucher = (event) => {
    const button = event.target;
    if (!(button instanceof Element)) return;

    const pill = button.closest('.cart-discount__pill');
    const code = pill instanceof HTMLElement ? pill.dataset.claimCode : undefined;
    if (!(pill instanceof HTMLElement) || !code) return;

    this.#forgetSavedVoucher(code);
    pill.remove();
    this.#syncListState();
  };

  /**
   * Shared core of removing one discount code — used by the ineligible pill's × button
   * (`removeDiscount`), an eligible pill's checkbox being unchecked (`toggleDiscount`), and the
   * exclusivity banner's own quick-remove action (`removeConflictingDiscount`).
   *
   * Guarded the same way as `#submitCode`: one cart-discount mutation at a time, and a stale section
   * is refreshed before a new action is accepted against it.
   * @param {string} discountCode
   * @param {{ save: boolean }} options - `save`: remember the code (see SAVED_VOUCHERS_KEY) so it can be
   *   re-applied from the list. False when the shopper is discarding it.
   */
  async #performRemoval(discountCode, { save }) {
    if (typeof this.dataset.sectionId !== 'string') return;
    if (this.#pending) return;

    if (this.#needsRefresh) {
      await this.#refreshStaleDiscounts();
      return;
    }

    this.#pending = true;
    const restoreControls = this.#disableControls();

    try {
      await this.#removeCode(discountCode, save);
    } finally {
      this.#pending = false;
      restoreControls();
    }
  }

  /**
   * The request half of `#performRemoval`. Not guarded on its own — only call it through it.
   * @param {string} discountCode
   * @param {boolean} save - Remember the code once the request has gone through.
   */
  async #removeCode(discountCode, save) {
    if (typeof this.dataset.sectionId !== 'string') return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    sectionRenderer.abortRender(this.dataset.sectionId);
    const abortController = this.#createAbortController();
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    try {
      this.dispatchEvent(
        new CartDiscountUpdateEvent({
          discountCodes: existingDiscounts.map((code) => ({ code })),
          promise: deferredPromise.promise,
        })
      );

      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      const sections = /** @type {Record<string, string> | null | undefined} */ (data.sections);
      const newHtml = sections?.[this.dataset.sectionId];

      // Before the morph: the re-render calls #reconcileIneligibleCodes, which is what offers the
      // saved code back, so it must already be in storage by then.
      if (save && typeof data.token === 'string') this.#saveVoucher(discountCode, data.token);

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });

      if (!newHtml) {
        await this.#refreshDiscountSection();
        await this.#restoreListAfterMorph();
        return;
      }

      sectionRenderer.abortRender(this.dataset.sectionId);
      await morphSection(this.dataset.sectionId, newHtml, {
        mode: this.closest('theme-drawer') ? 'hydration' : 'full',
      });
      await this.#restoreListAfterMorph();
    } catch (error) {
      deferredPromise.reject(error);
      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to remove discount',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Clicks on the dialog element itself only happen on its backdrop: `.cart-discount__dialog-inner`
   * fills the dialog edge to edge, so any click on real content targets a descendant instead.
   * @param {MouseEvent} event
   */
  #onBackdropClick = (event) => {
    if (event.target === this.refs.dialog) this.closeDialog();
  };

  /** Fires for every way the dialog closes — the close button, backdrop, or Escape. */
  #onDialogClose = () => {
    unlockScroll(this);
  };

  /**
   * Best-effort proactive check, run before a new code is even submitted: does IT, or anything
   * already applied, refuse to combine with other discounts at all (Shopify's own `combinesWith` all
   * false — see discount-info.service.ts in the membership/ERP repo for exactly what that means and
   * its documented limits: it is not a full pairwise "does A combine with B" resolver). Resolves to
   * `null` whenever there's nothing to warn about, including when the lookup itself is unavailable
   * (blank API URL, network failure) — this can only ever ADD a proactive warning on top of Shopify's
   * own real enforcement, never block a code Shopify would actually have accepted.
   * @param {string} newCode
   * @param {string[]} existingCodes
   * @returns {Promise<{ blockingCode: string, removableCode?: string } | null>}
   */
  async #findExclusivityConflict(newCode, existingCodes) {
    if (existingCodes.length === 0) return null;

    const newInfo = await fetchDiscountInfo(newCode).catch(() => null);
    if (newInfo?.isExclusive) {
      // The code being submitted is itself the exclusive one — there's no single existing code to
      // point a quick-remove action at (there may be several), so this is informational only.
      return { blockingCode: newCode };
    }

    for (const existingCode of existingCodes) {
      const info = await fetchDiscountInfo(existingCode).catch(() => null);
      if (info?.isExclusive) {
        return { blockingCode: existingCode, removableCode: existingCode };
      }
    }

    return null;
  }

  /** @param {{ blockingCode: string, removableCode?: string }} conflict */
  #showExclusiveBanner(conflict) {
    const { exclusiveBanner, exclusiveBannerText, exclusiveBannerAction } = this.refs;

    exclusiveBannerText.textContent = conflict.removableCode
      ? fillTemplate(Theme.translations.discount_conflict_existing, { code: conflict.blockingCode })
      : fillTemplate(Theme.translations.discount_conflict_new, { code: conflict.blockingCode });

    if (conflict.removableCode) {
      exclusiveBannerAction.textContent = fillTemplate(Theme.translations.discount_conflict_remove_action, {
        code: conflict.removableCode,
      });
      exclusiveBannerAction.dataset.discountCode = conflict.removableCode;
      exclusiveBannerAction.classList.remove('hidden');
    } else {
      exclusiveBannerAction.classList.add('hidden');
    }

    exclusiveBanner.classList.remove('hidden');
  }

  #hideExclusiveBanner() {
    this.refs.exclusiveBanner.classList.add('hidden');
  }

  /**
   * Keeps the collapsed trigger row's "{n} voucher tersimpan" line in sync with the TRUE total —
   * eligible plus ineligible — once #reconcileIneligibleCodes has actually fetched the cart and knows
   * it. The server-rendered initial value only ever counts the eligible codes Liquid itself can see
   * (see that method's own doc comment for why), so this is the one place the count can go up
   * without a full section morph.
   * @param {number} count
   */
  #updateTriggerCount(count) {
    this.refs.triggerCount.textContent =
      count > 0
        ? fillTemplate(Theme.translations.voucher_count_saved, { count: String(count) })
        : Theme.translations.voucher_count_empty;
  }

  /**
   * Shows the "Belum ada voucher tersimpan" placeholder in place of the list, or the list in place
   * of it, once #reconcileIneligibleCodes knows the TRUE total (eligible + ineligible). The
   * server-rendered initial state already gets this right for what Liquid alone can see (0 eligible
   * codes → placeholder shown) — this only has to correct it when an ineligible-only code turns up
   * that Liquid had no way to render at all.
   * @param {boolean} hasAny
   */
  #toggleEmptyState(hasAny) {
    const empty = this.querySelector('[data-cart-discount-empty]');
    const label = this.querySelector('[data-cart-discount-list-label]');
    const list = this.querySelector('.cart-discount__codes');
    if (empty) empty.classList.toggle('hidden', hasAny);
    if (label) label.classList.toggle('hidden', !hasAny);
    if (list) list.classList.toggle('hidden', !hasAny);
  }

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');

    const errorMessage = type === 'discount_code' ? 'Invalid discount code' : 'Discount not applicable for shipping';
    this.dispatchEvent(
      new CartErrorEvent({
        error: errorMessage,
        code: 'VALIDATION_CUSTOM',
      })
    );
  }

  /**
   * Fills in a gap Liquid has no way to close on its own: a discount code that's attached to the cart
   * session but not currently discounting anything — a wrong or expired code, or a real one whose own
   * condition (e.g. a minimum spend) isn't met yet. Shopify still keeps that code attached exactly like
   * any other, but it produces zero allocations, and `cart_level_discount_applications`/
   * `line_level_discount_allocations` — the only discount data Liquid can read at all — only ever
   * contain a code that IS discounting something. There is no `cart.discount_codes` or equivalent
   * (checked against shopify.dev: no such property on `cart`, no `discount_code` object either), so an
   * ineligible code was completely invisible on the cart page: no pill, no sign it was even there, no
   * way to remove it. This is the only place that information exists at all — the cart's own JSON.
   *
   * Also keeps the collapsed trigger row's count in sync — see `#updateTriggerCount`.
   *
   * Runs on connect and after every morph that touches this component (see updatedCallback above) —
   * a morph always re-renders this list from Liquid's still-ineligible-blind markup, wiping out
   * whatever this previously cloned in, so it isn't a one-time page-load fix-up.
   */
  async #reconcileIneligibleCodes() {
    const list = this.querySelector('.cart-discount__codes');
    const template = this.querySelector('[data-discount-ineligible-pill-template]');
    if (!list || !(template instanceof HTMLTemplateElement)) return;

    try {
      const response = await fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;

      const cart = await response.json();
      /** @type {Array<{ code: string, applicable: boolean }>} */
      const discountCodes = cart.discount_codes ?? [];

      // Vouchers the shopper unchecked come back as unchecked pills. Needs no applied code at all, so
      // it runs before the early return below.
      this.#renderSavedVouchers(typeof cart.token === 'string' ? cart.token : '', discountCodes);

      if (discountCodes.length === 0) {
        this.#syncListState();
        return;
      }

      const known = new Set(this.#existingDiscounts());

      for (const entry of discountCodes) {
        // Liquid already rendered a pill for every code it CAN see, which is exactly every code that
        // is applicable — skip those, and skip anything already cloned in by an earlier run.
        if (entry.applicable || known.has(entry.code)) continue;

        const fragment = template.content.cloneNode(true);
        if (!(fragment instanceof DocumentFragment)) continue;

        const pill = fragment.querySelector('.cart-discount__pill');
        const codeText = fragment.querySelector('.cart-discount__pill-code-text');
        const removeButton = fragment.querySelector('.cart-discount__pill-remove');
        if (!(pill instanceof HTMLLIElement) || !codeText || !(removeButton instanceof HTMLButtonElement)) continue;

        pill.dataset.discountCode = entry.code;
        codeText.textContent = entry.code;
        // The *-template attribute carries a pre-translated locale string with a literal '__CODE__'
        // placeholder (see the <template> in snippets/cart-summary.liquid) — the only thing this ever
        // writes into the page is the code itself, never hardcoded English copy.
        pill.setAttribute('aria-label', (pill.dataset.ariaTemplate ?? '').replaceAll('__CODE__', entry.code));
        delete pill.dataset.ariaTemplate;
        removeButton.setAttribute('aria-label', (removeButton.dataset.labelTemplate ?? '').replaceAll('__CODE__', entry.code));
        delete removeButton.dataset.labelTemplate;

        list.appendChild(fragment);
        known.add(entry.code);

        // Asks the membership system's discount-info API (assets/discount-info-api.js) for the code's
        // real Shopify condition and overwrites the generic "Belum berlaku" tag a beat later if it
        // finds one worth showing — e.g. "Voucher ini mulai berlaku 30 September 2026" for a code
        // that's simply SCHEDULED, which is exactly the case this store's own HELLO10/JOC-OCT hit.
        // Fire-and-forget per pill (not awaited here) so one slow lookup never holds up reconciling
        // the rest of the list; `pill`/`statusText` stay valid references after appendChild moves the
        // fragment's node into `list`, so writing to them later is safe.
        const statusText = pill.querySelector('.cart-discount__pill-status');
        this.#applyAutomaticRequirementNote(entry.code, statusText);
      }

      this.#syncListState();
    } catch (error) {
      // A network hiccup here just means the cart page stays as informative as it already was —
      // never worth surfacing to the shopper over.
      if (error instanceof Error && error.name !== 'AbortError') {
        console.warn('[cart-discount] Could not check for inactive discount codes:', error);
      }
    }
  }

  /**
   * @param {string} code - Already the code as stored on the pill (not necessarily uppercased by the
   *   caller — the discount-info API and its own echoed `code` field are case-preserving).
   * @param {Element | null} statusText - The pill's `.cart-discount__pill-status` element.
   */
  async #applyAutomaticRequirementNote(code, statusText) {
    if (!statusText) return;
    const text = await fetchDiscountRequirementText(code).catch(() => null);
    if (text) statusText.textContent = text;
  }

  /**
   * Adds any of a logged-in member's claimed-but-unused loyalty vouchers (from the membership
   * portal) that aren't already tracked by this cart, as "available to claim" pills — checking one
   * applies it immediately (see `claimMemberVoucher`). No-ops for a signed-out shopper, or when the
   * lookup is unavailable — `fetchMemberVouchers()` already resolves to `[]` in both cases.
   *
   * Deliberately lazy — called from `openPicker`/`updatedCallback`, never `connectedCallback` — each
   * call can cost the membership backend a handful of live Shopify Admin API calls (one per still-
   * active voucher; see RedemptionService.listRedemptions in that repo), so this only runs while the
   * picker is actually being looked at, not on every cart page load.
   */
  async #reconcileMemberVouchers() {
    const list = this.querySelector('.cart-discount__codes');
    const template = this.querySelector('[data-discount-available-pill-template]');
    if (!list || !(template instanceof HTMLTemplateElement)) return;

    const vouchers = await fetchMemberVouchers().catch(() => []);
    if (vouchers.length === 0) return;

    const known = new Set(this.#existingDiscounts());
    const alreadyOffered = new Set(
      Array.from(list.querySelectorAll('[data-claim-code]'))
        .map((element) => (element instanceof HTMLElement ? element.dataset.claimCode : null))
        .filter(Boolean)
    );

    let addedAny = false;
    for (const voucher of vouchers) {
      // Already applied/ineligible (data-discount-code), or already offered from an earlier call —
      // never offer the same voucher as a second "available" pill.
      if (known.has(voucher.code) || alreadyOffered.has(voucher.code)) continue;

      const fragment = template.content.cloneNode(true);
      if (!(fragment instanceof DocumentFragment)) continue;

      const pill = fragment.querySelector('.cart-discount__pill');
      const codeText = fragment.querySelector('.cart-discount__pill-code-text');
      const statusText = fragment.querySelector('.cart-discount__pill-status');
      const checkbox = fragment.querySelector('.cart-discount__pill-checkbox');
      if (!(pill instanceof HTMLElement) || !codeText || !statusText || !(checkbox instanceof HTMLInputElement)) {
        continue;
      }

      pill.dataset.claimCode = voucher.code;
      codeText.textContent = voucher.code;
      statusText.textContent = formatMemberVoucherLine(voucher);
      checkbox.setAttribute('aria-label', (checkbox.dataset.ariaTemplate ?? '').replaceAll('__CODE__', voucher.code));
      delete checkbox.dataset.ariaTemplate;

      list.appendChild(fragment);
      addedAny = true;
    }

    if (addedAny) this.#syncListState();
  }

  /**
   * Re-adds what a morph wipes from the list: the ineligible and saved pills (cloned in from the cart
   * JSON) and, while the picker is open, a member's claimable vouchers.
   *
   * `updatedCallback` is supposed to do this, but a morph calls it on the freshly parsed node rather
   * than on this live element (the same effect CLAUDE.md notes for `gwp-progress`), so after our own
   * apply/remove the pills would only come back on the next full page load. Every flow that morphs the
   * section therefore calls this itself, once the morph has finished.
   */
  async #restoreListAfterMorph() {
    if (!this.isConnected) return;
    await this.#reconcileIneligibleCodes();
    if (this.refs.dialog.open) await this.#reconcileMemberVouchers();
  }

  /**
   * Re-counts what is actually in the list — applied, ineligible, saved and member-offered pills all
   * count — and brings the trigger row's "{n} voucher tersimpan" line and the empty placeholder in
   * line with it. Liquid alone only knows the applied ones.
   */
  #syncListState() {
    const list = this.querySelector('.cart-discount__codes');
    if (!list) return;

    const total = list.querySelectorAll('.cart-discount__pill').length;
    this.#updateTriggerCount(total);
    this.#toggleEmptyState(total > 0);
  }

  /**
   * Remembers `code` for the cart identified by `token`, unless it already is.
   * @param {string} code
   * @param {string} token
   */
  #saveVoucher(code, token) {
    const codes = readSavedVouchers(token);
    if (codes.some((saved) => sameCode(saved, code))) return;
    writeSavedVouchers(token, [...codes, code]);
  }

  /**
   * Drops `code` from whatever cart it was remembered for.
   * @param {string} code
   */
  #forgetSavedVoucher(code) {
    const entry = readSavedVouchersEntry();
    if (!entry) return;
    writeSavedVouchers(
      entry.token,
      entry.codes.filter((saved) => !sameCode(saved, code))
    );
  }

  /**
   * Adds an unchecked, enabled pill for every remembered code that is not on the cart right now (a
   * code that IS on the cart is already shown as an applied or ineligible pill). Idempotent: a morph
   * wipes cloned pills and `updatedCallback` runs this again, and an earlier run's pills are skipped.
   * @param {string} token - The cart's token (from /cart.json); a different cart has no saved codes.
   * @param {Array<{ code: string, applicable: boolean }>} discountCodes - What the cart has attached.
   */
  #renderSavedVouchers(token, discountCodes) {
    const list = this.querySelector('.cart-discount__codes');
    const template = this.querySelector('[data-discount-saved-pill-template]');
    if (!list || !(template instanceof HTMLTemplateElement) || !token) return;

    const onCart = discountCodes.map((entry) => entry.code);
    const offered = Array.from(list.querySelectorAll('[data-claim-code]'))
      .map((element) => (element instanceof HTMLElement ? (element.dataset.claimCode ?? '') : ''))
      .filter(Boolean);

    for (const code of readSavedVouchers(token)) {
      if (onCart.some((c) => sameCode(c, code)) || offered.some((c) => sameCode(c, code))) continue;

      const fragment = template.content.cloneNode(true);
      if (!(fragment instanceof DocumentFragment)) continue;

      const pill = fragment.querySelector('.cart-discount__pill');
      const codeText = fragment.querySelector('.cart-discount__pill-code-text');
      const checkbox = fragment.querySelector('.cart-discount__pill-checkbox');
      const removeButton = fragment.querySelector('.cart-discount__pill-remove');
      if (
        !(pill instanceof HTMLElement) ||
        !codeText ||
        !(checkbox instanceof HTMLInputElement) ||
        !(removeButton instanceof HTMLButtonElement)
      ) {
        continue;
      }

      // `data-claim-code`, deliberately not `data-discount-code`: this code is NOT on the cart, and
      // #existingDiscounts() builds the "already attached" list from the latter (see the template).
      pill.dataset.claimCode = code;
      codeText.textContent = code;
      checkbox.setAttribute('aria-label', (checkbox.dataset.ariaTemplate ?? '').replaceAll('__CODE__', code));
      delete checkbox.dataset.ariaTemplate;
      removeButton.setAttribute('aria-label', (removeButton.dataset.labelTemplate ?? '').replaceAll('__CODE__', code));
      delete removeButton.dataset.labelTemplate;

      list.appendChild(fragment);
      offered.push(code);
    }
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
